import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import {
    Container,
    Card,
    Badge,
    Button,
    Tab,
    Table,
    Form,
    Alert,
    Dropdown,
    Row,
    Col,
} from 'react-bootstrap';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import DashboardNav, {
    generateChildId,
} from '@/components/common/DashboardNav';
import ProgramDeadlinesSection from '@/components/common/ProgramDeadlinesSection';
import ProgramTermHeader, {
    MissingDatesAlert,
} from '@/components/common/ProgramTermHeader';
import ProgramCoursePrefixes from '@/components/common/ProgramCoursePrefixes';
import DecisionEmailTemplates from '@/features/programs/DecisionEmailTemplates';
import OnboardingEmailDialog, {
    formatSentDate,
} from '@/features/programs/OnboardingEmailDialog';
import {
    nomineesOf,
    requestedLACount,
} from '@/features/applications/faculty/NomineeOfferPicker';
import programService from '@/shared/services/programService';
import TrainingDatesCard from '@/features/programs/TrainingDatesCard';
import ProgramNotificationSettings from '@/features/programs/ProgramNotificationSettings';
import ProgramPageLinkCard from '@/features/programs/ProgramPageLinkCard';
import OfferResponseDaysCard from '@/features/programs/OfferResponseDaysCard';
import ProgramDecisionsList from '@/features/programs/ProgramDecisionsList';
import applicationService from '@/shared/services/applicationService';
import assignmentService from '@/shared/services/assignmentService';
import courseService from '@/shared/services/courseService';
import termService from '@/shared/services/termService';
import budgetService from '@/shared/services/budgetService';
import studentApplicationService from '@/shared/services/studentApplicationService';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import GuideLink from '@/components/common/GuideLink';
import CoordinatorPositionsTab from '@/features/programs/CoordinatorPositionsTab';
import CoordinatorOverview from '@/features/programs/CoordinatorOverview';
import BudgetStatus, { budgetOrNone } from '@/features/programs/BudgetStatus';
import CoordinatorSettings from '@/features/programs/CoordinatorSettings';
import ListFilterNote from '@/features/programs/ListFilterNote';
import UnsubmittedDrafts from '@/features/programs/UnsubmittedDrafts';
import {
    APPLICATION_LISTS,
    DRAFTS_SHOW,
    listFor,
} from '@/features/programs/overviewLists';
import { useDashboardTab } from '@/shared/hooks/useDashboardTab';
import { useListLinkReset } from '@/shared/hooks/useListLinkReset';
import {
    needsStudents,
    positionStatusText,
} from '@/features/programs/positionDisplay';
import { positionService } from '@/shared/services/positionService';
import '@/features/programs/CoordinatorDashboard.css';
import FacultyCell from '@/features/programs/FacultyCell';
import RequestCardList from '@/features/programs/RequestCardList';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { badgeTextFor } from '@/shared/utils/badgeText';
import { downloadBlob } from '@/shared/utils/downloadBlob';
import { positionChoiceSummary } from '@/shared/utils/positionChoiceSummary';
import SortableTh from '@/components/common/SortableTh';
import TableControlsBar from '@/components/common/TableControlsBar';
import TableEmpty from '@/components/common/TableEmpty';
import {
    PHONE_QUERY,
    SECONDARY_COLUMN,
    SECONDARY_COLUMN_LG,
    SECONDARY_COLUMN_XL,
} from '@/shared/utils/tableControls';
import { useTableControls } from '@/shared/hooks/useTableControls';
import { useTableParams } from '@/shared/hooks/useTableParams';
import {
    ASSIGNMENT_STATUS_ORDER,
    STUDENT_APPLICATION_STATUS_OPTIONS,
    assignmentStatusLabel,
    courseCode,
    courseColumn,
    courseOptionsFor,
    dateColumn,
    departmentOf,
    departmentOptionsFor,
    facultyColumn,
    modalityColumn,
    modalityText,
    personColumn,
    sessionsText,
    statusColumn,
    laStudentApplicationColumns,
    workStudyText,
} from '@/features/programs/coordinatorColumns';
import { otherRoleInterestText } from '@/features/applications/student/programs/laAnswers';
import StatusPill from '@/components/common/StatusPill';
import LoadingState from '@/components/common/LoadingState';
import LoadError from '@/components/common/LoadError';
import settledRead from '@/shared/utils/settledRead';
import { STUDENT_APPLICATION_PILL, pillFor } from '@/shared/utils/statusPills';
import {
    PROGRAM_OUTCOME_OPTIONS,
    PROGRAM_OUTCOME_ORDER,
} from '@/features/programs/programOutcomeStatus';
import { formatAnswerDay } from '@/shared/utils/deadline';
import { termDisplayName } from '@/shared/utils/termLabel';

const appStatusPill = (app) => (
    <StatusPill
        family="programOutcome"
        value={app.programOutcome}
        application={app}
    />
);

// What LA stores nothing for: the section's faculty review applicants. The
// server's TOGGLE_DEFAULTS.la.visibility (services/hiringAccess.js) is the
// source; this only labels the select.
const LA_DEFAULT_VISIBILITY = 'coordinator_and_faculty';

// A withdrawal or decline moves the CRN to formerCrn, so the row keeps it
const crnOf = (app) =>
    app.courseConfirmation?.crn || app.courseConfirmation?.formerCrn || '';

// Nobody will place LAs for these, so they add nothing to LAs requested
const NOT_PLACED_STATUSES = new Set(['withdrawn', 'rejected']);

// The raw count, not requestedLACount(): a request with no count adds 0
const requestedForTotal = (app) => {
    if (NOT_PLACED_STATUSES.has(app.status)) return 0;
    const count = app.programSpecificResponses?.la?.requestedLACount;
    return Number.isInteger(count) && count >= 1 ? count : 0;
};

// "BIO 1080, section 001", the way a card names the request
const requestHeading = (app) => {
    const section =
        app.courseConfirmation?.sectionNumber || app.course?.section;
    return [courseCode(app.course) || 'Course', section && `section ${section}`]
        .filter(Boolean)
        .join(', ');
};

const getLARank = (app) => {
    const laPref = app.programPreferences?.find((p) => p.programType === 'la');
    return laPref?.rank || '-';
};

// The Nominees cell: a count, or the reason there is none yet
const nomineesText = (app) => {
    const status = app.programSpecificResponses?.la?.nomineeStatus;
    if (status === 'needs_help') return 'Needs help';
    if (status === 'selecting') return 'Selecting';
    return String(nomineesOf(app).length);
};

const applicationDepartment = (app) => departmentOf(app.course);

const APPLICATION_COLUMNS = [
    facultyColumn(),
    courseColumn((app) => app.course),
    modalityColumn('xl'),
    {
        key: 'crn',
        label: 'CRN',
        type: 'number',
        secondary: true,
        searchable: true,
        value: (app) => crnOf(app),
    },
    {
        key: 'rank',
        label: 'LA Rank',
        type: 'number',
        secondary: true,
        value: (app) => {
            const rank = getLARank(app);
            return rank === '-' ? null : rank;
        },
    },
    {
        key: 'requested',
        label: 'Requested',
        type: 'number',
        secondary: 'lg',
        value: (app) => requestedLACount(app),
    },
    {
        key: 'nominees',
        label: 'Nominees',
        type: 'number',
        secondary: 'lg',
        value: (app) => nomineesOf(app).length,
        csv: nomineesText,
    },
    {
        ...dateColumn('submitted', 'Submitted', (app) => app.submittedAt),
        secondary: 'xl',
    },
    statusColumn(
        PROGRAM_OUTCOME_ORDER,
        (app) => app.programOutcome,
        (app) =>
            pillFor('programOutcome', app.programOutcome, { application: app })
                .label
    ),
];

const ASSIGNMENT_COLUMNS = [
    personColumn('la', 'LA', (la) => la.peerSupporter),
    {
        key: 'instructor',
        label: 'Instructor',
        type: 'text',
        secondary: true,
        value: (la) => la.instructorName,
    },
    courseColumn((la) => la.course),
    {
        key: 'hours',
        label: 'Hours/Week',
        type: 'number',
        secondary: true,
        searchable: false,
        value: (la) => la.hoursPerWeek || null,
    },
    {
        key: 'schedule',
        label: 'Schedule',
        type: 'text',
        secondary: true,
        value: (la) => sessionsText(la.schedule?.sessions),
    },
    statusColumn(
        ASSIGNMENT_STATUS_ORDER,
        (la) => la.status,
        (la) => assignmentStatusLabel(la.status)
    ),
];

const STUDENT_APPLICATION_COLUMNS = laStudentApplicationColumns(
    STUDENT_APPLICATION_PILL,
    positionChoiceSummary
);

const COURSE_STAFFING_OPTIONS = [
    { value: 'needs_students', label: 'Needs students' },
];

const assignmentCourseOf = (la) => courseCode(la.course);
const applicationStatusValue = (app) => app.programOutcome;
const studentApplicationStatusOf = (app) => app.status;

// What the Positions cell of a course row shows, as text
const positionsText = (coursePositions, loaded) => {
    if (!loaded) return "Positions couldn't be loaded";
    if (coursePositions.length === 0) return 'No award';
    return coursePositions
        .map(
            (position) =>
                `Section ${position.section}: ${position.filled} of ${position.awarded} filled, ${position.openSlots} open, ${positionStatusText(position)}`
        )
        .join('; ');
};

// Tab keys that moved into the Settings tab, with the section each landed in
const SETTINGS_ALIASES = {
    notifications: { tab: 'settings', show: 'notifications' },
    'decision-emails': { tab: 'settings', show: 'decision-emails' },
    'offer-email': { tab: 'settings', show: 'offer-email' },
    'onboarding-email': { tab: 'settings', show: 'onboarding-email' },
};

function LACoordinatorDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const studentApplicationEnabled = useFeature('studentApplication');
    const hiringEnabled = useFeature('hiring');
    const [successMessage, setSuccessMessage] = useState(
        location.state?.success || null
    );
    const [terms, setTerms] = useState([]);
    const [selectedTerm, setSelectedTerm] = useState(null);
    const [stats, setStats] = useState(null);
    const [applications, setApplications] = useState([]);
    // The server's real total when the whole-term list hit its backstop
    const [cutOffTotal, setCutOffTotal] = useState(null);
    const [assignments, setAssignments] = useState([]);
    // The accepted LA whose onboarding email is open in the send dialog
    const [onboardingFor, setOnboardingFor] = useState(null);
    const [courses, setCourses] = useState([]);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { activeTab, show, selectTab, clearShow } = useDashboardTab(
        [
            'overview',
            'applications',
            'decisions',
            hiringEnabled && 'positions',
            studentApplicationEnabled && 'student-applications',
            'active',
            'courses',
            'settings',
        ].filter(Boolean),
        'overview',
        SETTINGS_ALIASES
    );
    const [deadlinesOpen, setDeadlinesOpen] = useState(false);
    const [budget, setBudget] = useState(null);
    const [config, setConfig] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [budgetFailed, setBudgetFailed] = useState(false);
    const [configFailed, setConfigFailed] = useState(false);
    const [studentAppsFailed, setStudentAppsFailed] = useState(false);
    const [toggleForm, setToggleForm] = useState({
        visibility: LA_DEFAULT_VISIBILITY,
    });
    const [savingToggles, setSavingToggles] = useState(false);
    const [studentApplications, setStudentApplications] = useState([]);
    const [exporting, setExporting] = useState(false);
    // Its own alert, so a retry doesn't clear a failed term load
    const [exportError, setExportError] = useState(null);
    // The term on screen now, for an export that finishes after a switch
    const shownTermCode = useRef(null);

    // Load terms on mount
    useEffect(() => {
        const loadTerms = async () => {
            try {
                const data = await termService.getAllTerms();
                setTerms(data);
                const active = data.find((t) => t.isActive) || data[0];
                if (active) {
                    setSelectedTerm(active);
                } else {
                    setLoading(false);
                }
            } catch (_err) {
                setError('Failed to load terms.');
                setLoading(false);
            }
        };
        loadTerms();
    }, []);

    const loadTermData = useCallback(
        async (termCode) => {
            try {
                setLoading(true);
                setError(null);
                setLoadFailed(false);

                const [
                    statsData,
                    appsData,
                    assignmentsData,
                    coursesData,
                    budgetData,
                    studentAppsData,
                    configData,
                    positionsData,
                ] = await Promise.all([
                    programService.getLAStats(termCode),
                    applicationService.getApplicationsByProgram('la', {
                        term: termCode,
                    }),
                    // The tab and the Active LAs card count the same places:
                    // the program stats count active assignments, so the tab
                    // asks for exactly those.
                    assignmentService.getAssignmentsByProgram('la', {
                        term: termCode,
                        status: 'active',
                    }),
                    courseService.getCoursesEligibleForProgram(termCode, 'la'),
                    settledRead(
                        budgetOrNone(budgetService.getBudget('la', termCode))
                    ),
                    // Not asked for at all while the feature is off: the server
                    // gates this endpoint with the same flag, so the request
                    // can only 404, and the tab that would show the result is
                    // not rendered either. The .catch here is for a request
                    // that was worth making and failed, not for one we already
                    // know the answer to.
                    studentApplicationEnabled
                        ? settledRead(
                              studentApplicationService.getApplicationsByProgram(
                                  'la',
                                  { term: termCode }
                              )
                          )
                        : { value: [], failed: false },
                    settledRead(programService.getConfig('la')),
                    // The same read as the Positions tab, so the two agree.
                    // A failed read leaves the STEM Courses status unknown
                    // rather than reading as "no award".
                    hiringEnabled
                        ? positionService
                              .listPositions({ program: 'la', term: termCode })
                              .then((answer) => answer.positions || [])
                              .catch(() => null)
                        : [],
                ]);

                setStats(statsData);
                setApplications(appsData);
                setCutOffTotal(appsData.truncated ? appsData.total : null);
                setAssignments(assignmentsData);
                // Already narrowed to this program, and to the whole term
                // rather than one page of it, by the service call above.
                setCourses(coursesData);
                setBudget(budgetData.value);
                setBudgetFailed(budgetData.failed);
                setStudentApplications(studentAppsData.value || []);
                setStudentAppsFailed(studentAppsData.failed);
                setConfig(configData.value);
                setConfigFailed(configData.failed);
                setPositions(positionsData);
            } catch (_err) {
                setLoadFailed(true);
            } finally {
                setLoading(false);
            }
        },
        [studentApplicationEnabled, hiringEnabled]
    );

    useEffect(() => {
        shownTermCode.current = selectedTerm?.code ?? null;
        if (selectedTerm?.code) {
            loadTermData(selectedTerm.code);
        }
    }, [selectedTerm?.code, loadTermData]);

    useEffect(() => {
        if (config) {
            setToggleForm({
                visibility: config.toggles?.visibility || LA_DEFAULT_VISIBILITY,
            });
        }
    }, [config]);

    // The Positions tab re-reads a position after each hiring action; this
    // keeps the STEM Courses counts and filter in step with it.
    const handlePositionChanged = useCallback((fresh) => {
        setPositions((prev) =>
            prev
                ? prev.map((p) =>
                      p.applicationId === fresh.applicationId ? fresh : p
                  )
                : prev
        );
    }, []);

    const handleTermChange = (code) => {
        const term = terms.find((t) => t.code === code);
        if (term) {
            setSelectedTerm(term);
            // That failure was for the term being left
            setExportError(null);
        }
    };

    const handleSaveToggles = async () => {
        if (!selectedTerm?.code) return;
        setSavingToggles(true);
        try {
            await programService.upsertConfig('la', { toggles: toggleForm });
            await loadTermData(selectedTerm.code);
        } catch (_err) {
            setError('Failed to save offer settings.');
        } finally {
            setSavingToggles(false);
        }
    };

    // The server builds the file from the whole term, not this page's list
    const handleExport = async () => {
        const termCode = selectedTerm?.code;
        // The button stays focusable while exporting, so ignore a second press
        if (!termCode || exporting) return;
        setExporting(true);
        setExportError(null);
        try {
            const { blob, filename } =
                await applicationService.exportApplicationsByProgram(
                    'la',
                    termCode
                );
            // The server's name when the browser can read it
            downloadBlob(blob, filename || `la-applications-${termCode}.csv`);
        } catch (_err) {
            // A failure for a term no longer shown would read as this one's
            if (shownTermCode.current === termCode) {
                setExportError(
                    'Failed to export applications. Please try again.'
                );
            }
        } finally {
            setExporting(false);
        }
    };

    const handleTermUpdated = (updatedTerm) => {
        setSelectedTerm(updatedTerm);
        setTerms((prev) =>
            prev.map((t) => (t.code === updatedTerm.code ? updatedTerm : t))
        );
    };

    const renderPositions = (coursePositions) => {
        if (!positions) {
            return (
                <span className="text-muted">
                    Positions couldn&apos;t be loaded
                </span>
            );
        }
        if (coursePositions.length === 0) {
            return <span className="text-muted">No award</span>;
        }
        return coursePositions.map((position) => {
            let variant = 'secondary';
            if (position.fullyStaffed) variant = 'success';
            else if (needsStudents(position)) variant = 'warning';
            return (
                <div key={position.applicationId}>
                    <small className="me-2">
                        Section {position.section}: {position.filled} of{' '}
                        {position.awarded} filled, {position.openSlots} open
                    </small>
                    <Badge
                        bg={variant}
                        text={badgeTextFor(variant)}
                        className="text-nowrap"
                    >
                        {positionStatusText(position)}
                    </Badge>
                </div>
            );
        });
    };

    const applicationList =
        activeTab === 'applications'
            ? listFor(APPLICATION_LISTS.la, show)
            : null;
    const listedApplications = applicationList
        ? applications.filter(applicationList.matches)
        : applications;
    const applicationDepartmentOptions = departmentOptionsFor(
        applications,
        (app) => app.course
    );
    const applicationControls = useTableControls({
        rows: listedApplications,
        columns: APPLICATION_COLUMNS,
        statusOf: applicationStatusValue,
        departmentOf: applicationDepartment,
        departmentOptions: applicationDepartmentOptions,
        persist: useTableParams('apps'),
    });
    const asCards = useMediaQuery(PHONE_QUERY);
    const requestCardOf = (app) => {
        const requested = requestedLACount(app);
        const rank = getLARank(app);
        return {
            key: app._id,
            heading: requestHeading(app),
            title: app.course?.title,
            faculty: <FacultyCell app={app} />,
            pill: appStatusPill(app),
            facts: [
                modalityText(app),
                `${requested} ${requested === 1 ? 'LA' : 'LAs'} requested`,
                rank !== '-' && `LA choice ${rank}`,
                app.submittedAt &&
                    `Submitted ${formatAnswerDay(app.submittedAt)}`,
            ].filter(Boolean),
            actionLabel: 'Review request',
            to: `/applications/${app._id}`,
        };
    };
    const {
        setSearch: setApplicationSearch,
        setStatus: setApplicationStatus,
        setDepartment: setApplicationDepartment,
    } = applicationControls;

    // A list an Overview link opened starts from exactly the rows it counted
    useListLinkReset(
        show,
        useCallback(() => {
            setApplicationSearch('');
            setApplicationStatus('all');
            setApplicationDepartment('all');
        }, [
            setApplicationSearch,
            setApplicationStatus,
            setApplicationDepartment,
        ])
    );
    // Follows the search, status and department filter, like the count beside it
    const lasRequested = applicationControls.rows.reduce(
        (sum, app) => sum + requestedForTotal(app),
        0
    );

    // No nominees yet says why, so a "needs help" request stands out
    const getNominees = (app) => {
        const status = app.programSpecificResponses?.la?.nomineeStatus;
        if (status === 'needs_help') {
            return <span className="fw-semibold">Needs help</span>;
        }
        if (status === 'selecting') {
            return <span className="text-muted">Selecting</span>;
        }
        return nomineesOf(app).length;
    };

    const assignmentControls = useTableControls({
        rows: assignments,
        columns: ASSIGNMENT_COLUMNS,
        statusOf: assignmentCourseOf,
        persist: useTableParams('las'),
    });
    const assignmentCourseOptions = courseOptionsFor(
        assignments,
        (la) => la.course
    );
    const positionsByCourseId = useMemo(() => {
        const byCourse = new Map();
        (positions || []).forEach((p) => {
            byCourse.set(p.courseId, [...(byCourse.get(p.courseId) || []), p]);
        });
        return byCourse;
    }, [positions]);
    const courseNeedsStudents = useCallback(
        (course) =>
            (positionsByCourseId.get(course._id) || []).some(needsStudents)
                ? 'needs_students'
                : 'other',
        [positionsByCourseId]
    );
    const courseColumns = useMemo(
        () => [
            courseColumn(
                (course) => course,
                (course) => course.section
            ),
            ...(hiringEnabled
                ? [
                      {
                          key: 'positions',
                          label: 'Positions',
                          type: 'number',
                          searchable: false,
                          value: (course) =>
                              positions
                                  ? (
                                        positionsByCourseId.get(course._id) ||
                                        []
                                    ).reduce((sum, p) => sum + p.openSlots, 0)
                                  : null,
                          csv: (course) =>
                              positionsText(
                                  positionsByCourseId.get(course._id) || [],
                                  Boolean(positions)
                              ),
                      },
                  ]
                : []),
        ],
        [hiringEnabled, positions, positionsByCourseId]
    );
    const courseControls = useTableControls({
        rows: courses,
        columns: courseColumns,
        statusOf: courseNeedsStudents,
        persist: useTableParams('courses'),
    });
    const studentApplicationControls = useTableControls({
        rows: studentApplications,
        columns: STUDENT_APPLICATION_COLUMNS,
        statusOf: studentApplicationStatusOf,
        persist: useTableParams('students'),
    });

    // Where the requests stand is the Overview's Requests line, from one
    // source; the header keeps the figures nothing else on the tab shows
    const displayStats = [
        {
            value:
                stats?.activeLAs ??
                assignments.filter((a) => a.status === 'active').length,
            label: 'Active LAs',
            color: 'success',
        },
        {
            value: stats?.stemCourses ?? courses.length,
            label: 'STEM Courses',
            color: 'primary',
        },
        {
            value: stats?.completedPedagogy ?? 0,
            label: 'Pedagogy Complete',
            color: 'info',
        },
    ];

    const manageDates = () => {
        selectTab('settings', 'deadlines');
        setDeadlinesOpen(true);
    };

    const navGroups = [
        {
            key: 'overview',
            label: 'Overview',
            tabs: [{ key: 'overview', label: 'Overview' }],
        },
        {
            key: 'requests',
            label: 'Requests',
            tabs: [
                {
                    key: 'applications',
                    label: `Support requests (${cutOffTotal ?? applications.length})`,
                },
                { key: 'decisions', label: 'Decisions' },
            ],
        },
        {
            key: 'hiring',
            label: 'Hiring',
            tabs: [
                hiringEnabled && { key: 'positions', label: 'Positions' },
                studentApplicationEnabled && {
                    key: 'student-applications',
                    label: `Student Applicants (${studentAppsFailed ? '-' : studentApplications.length})`,
                },
                { key: 'active', label: `Active LAs (${assignments.length})` },
            ].filter(Boolean),
        },
        {
            key: 'courses',
            label: `Courses (${courses.length})`,
            tabs: [{ key: 'courses', label: `Courses (${courses.length})` }],
        },
        {
            key: 'settings',
            label: 'Settings',
            tabs: [{ key: 'settings', label: 'Settings' }],
        },
    ];

    const settingsSections = [
        {
            key: 'offer-settings',
            title: 'Who reviews applicants',
            content: (
                <>
                    {configFailed ? (
                        <LoadError
                            what="the offer settings"
                            onRetry={() => loadTermData(selectedTerm.code)}
                        />
                    ) : (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="py-3">
                                <h2 className="h5 mb-3">
                                    Who reviews applicants
                                </h2>
                                <Row className="align-items-end">
                                    <Col md={10}>
                                        <Form.Label htmlFor="la-applicant-visibility">
                                            Applicant Visibility
                                        </Form.Label>
                                        <Form.Select
                                            id="la-applicant-visibility"
                                            aria-describedby="la-applicant-visibility-help"
                                            value={toggleForm.visibility}
                                            onChange={(e) =>
                                                setToggleForm((prev) => ({
                                                    ...prev,
                                                    visibility: e.target.value,
                                                }))
                                            }
                                        >
                                            <option value="coordinator_and_faculty">
                                                Faculty review, coordinators can
                                                step in
                                            </option>
                                            <option value="coordinator_only">
                                                Coordinators only
                                            </option>
                                        </Form.Select>
                                        <Form.Text id="la-applicant-visibility-help">
                                            Faculty review applicants for their
                                            own courses, interview them and make
                                            offers. Coordinators see every
                                            applicant and can step in at any
                                            stage. Choose Coordinators only to
                                            keep applicants from faculty.
                                        </Form.Text>
                                    </Col>
                                    <Col md={2}>
                                        <Button
                                            variant="primary"
                                            className="w-100"
                                            onClick={handleSaveToggles}
                                            disabled={savingToggles || loading}
                                        >
                                            {savingToggles
                                                ? 'Saving...'
                                                : 'Save'}
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </>
            ),
        },
        ...(hiringEnabled
            ? [
                  {
                      key: 'offer-days',
                      title: 'Days to answer an offer',
                      content: <OfferResponseDaysCard programType="la" />,
                  },
              ]
            : []),
        {
            key: 'courses-served',
            title: 'Courses served',
            content: (
                <>
                    <ProgramCoursePrefixes programType="la" programLabel="LA" />
                    <Alert
                        variant="light"
                        className="mb-0 border coordinator-info-alert-la"
                    >
                        <strong>LA Program:</strong> Learning Assistants support
                        active learning in STEM courses. LAs must complete or be
                        enrolled in the LA Pedagogy course. Students apply
                        directly and faculty partners are matched based on
                        course needs.
                    </Alert>
                </>
            ),
        },
        {
            key: 'deadlines',
            title: 'Application deadlines',
            content: (
                <ProgramDeadlinesSection
                    programType="la"
                    programLabel="LA"
                    programName="Learning Assistants"
                    selectedTerm={selectedTerm}
                    onTermUpdated={handleTermUpdated}
                    show={deadlinesOpen}
                    onShow={() => setDeadlinesOpen(true)}
                    onHide={() => setDeadlinesOpen(false)}
                />
            ),
        },
        {
            key: 'notifications',
            title: 'Notifications',
            content: <ProgramNotificationSettings programType="la" />,
        },
        {
            key: 'training-dates',
            title: 'Training dates',
            content: (
                <TrainingDatesCard
                    programType="la"
                    termCode={selectedTerm?.code}
                    termName={termDisplayName(selectedTerm)}
                />
            ),
        },
        {
            key: 'decision-emails',
            title: 'Decision emails',
            content: <DecisionEmailTemplates programType="la" />,
        },
        ...(hiringEnabled
            ? [
                  {
                      key: 'offer-email',
                      title: 'Offer email',
                      content: (
                          <DecisionEmailTemplates
                              programType="la"
                              kind="offer"
                          />
                      ),
                  },
                  {
                      key: 'onboarding-email',
                      title: 'Onboarding email',
                      content: (
                          <DecisionEmailTemplates
                              programType="la"
                              kind="onboarding"
                          />
                      ),
                  },
              ]
            : []),
        {
            key: 'program-page',
            title: 'Program page link',
            content: <ProgramPageLinkCard programType="la" programLabel="LA" />,
        },
    ];

    if (!selectedTerm && !loading) {
        return (
            <Container className="py-5">
                {error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : (
                    <Alert variant="warning">
                        No terms configured. Ask an administrator to create a
                        term in the Admin panel.
                    </Alert>
                )}
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <ProgramTermHeader
                programType="la"
                programLabel="LA"
                programName="Learning Assistants"
                programColor="var(--psh-la-color)"
                terms={terms}
                selectedTerm={selectedTerm}
                onTermChange={handleTermChange}
                stats={displayStats}
                statsLoading={loading}
                statsFailed={loadFailed}
                compact={activeTab !== 'overview'}
            />
            <GuideLink
                audience="coordinator"
                className="d-block small mb-3"
                short
            />

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}
            {successMessage && (
                <Alert
                    variant="success"
                    dismissible
                    onClose={() => setSuccessMessage(null)}
                >
                    {successMessage}
                </Alert>
            )}

            {loadFailed ? (
                <LoadError
                    what="the LA program data"
                    onRetry={() => loadTermData(selectedTerm.code)}
                />
            ) : loading ? (
                <LoadingState rows={4} label="Loading LA program data" />
            ) : (
                <Tab.Container
                    activeKey={activeTab}
                    generateChildId={generateChildId}
                >
                    <DashboardNav
                        ariaLabel="Dashboard sections"
                        groups={navGroups}
                        activeTab={activeTab}
                        onSelect={selectTab}
                    />
                    {activeTab === 'overview' && (
                        <>
                            <MissingDatesAlert
                                programType="la"
                                programLabel="LA"
                                selectedTerm={selectedTerm}
                                onManageDates={manageDates}
                            />
                            {!loadFailed && (
                                <BudgetStatus
                                    budget={budget}
                                    failed={budgetFailed}
                                    loading={loading}
                                    onRetry={() =>
                                        loadTermData(selectedTerm.code)
                                    }
                                    termName={termDisplayName(selectedTerm)}
                                    unit="LA"
                                    unitPlural="LAs"
                                />
                            )}
                        </>
                    )}
                    <Tab.Content>
                        <Tab.Pane eventKey="overview" mountOnEnter>
                            <CoordinatorOverview
                                active={activeTab === 'overview'}
                                programType="la"
                                term={selectedTerm?.code}
                                termName={termDisplayName(selectedTerm)}
                                tabs={{
                                    requests: 'applications',
                                    positions: hiringEnabled
                                        ? 'positions'
                                        : null,
                                }}
                            />
                        </Tab.Pane>

                        {/* Applications Tab */}
                        <Tab.Pane eventKey="applications">
                            {applicationList && (
                                <ListFilterNote
                                    show={show}
                                    list={applicationList}
                                    count={listedApplications.length}
                                    programLabel="LA"
                                    termName={termDisplayName(selectedTerm)}
                                    noun="requests"
                                    onClear={clearShow}
                                />
                            )}
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white">
                                    <TableControlsBar
                                        controls={applicationControls}
                                        noun="support requests"
                                        searchTitle="Search support requests"
                                        searchLabel="Search support requests by faculty name, submitter, course or CRN"
                                        searchPlaceholder="Name, course or CRN"
                                        statusLabel="Filter by request status"
                                        statusOptions={PROGRAM_OUTCOME_OPTIONS}
                                        departmentOptions={
                                            applicationDepartmentOptions
                                        }
                                        exportFilename={`la-applications-${selectedTerm?.code}.csv`}
                                        onExportFull={handleExport}
                                        fullNote="Every field, including named students' 900 numbers"
                                        exporting={exporting}
                                        summary={`${applicationControls.rows.length} of ${cutOffTotal ?? applications.length} support requests, ${lasRequested} ${lasRequested === 1 ? 'LA' : 'LAs'} requested`}
                                    />
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {exportError && (
                                        <Alert
                                            variant="danger"
                                            className="m-3"
                                            dismissible
                                            onClose={() => setExportError(null)}
                                        >
                                            {exportError}
                                        </Alert>
                                    )}
                                    {cutOffTotal !== null && (
                                        <Alert
                                            variant="warning"
                                            className="m-3"
                                        >
                                            Showing the newest{' '}
                                            {applications.length} of{' '}
                                            {cutOffTotal} support requests for
                                            this term. Older ones aren&apos;t
                                            listed, so search and the filters
                                            don&apos;t cover them.
                                        </Alert>
                                    )}
                                    {asCards ? (
                                        <RequestCardList
                                            label="Support requests"
                                            items={applicationControls.rows.map(
                                                requestCardOf
                                            )}
                                        />
                                    ) : (
                                        <Table
                                            responsive
                                            hover
                                            className="mb-0"
                                        >
                                            <thead className="bg-light">
                                                <tr>
                                                    {APPLICATION_COLUMNS.map(
                                                        (column) => (
                                                            <SortableTh
                                                                key={column.key}
                                                                column={column}
                                                                controls={
                                                                    applicationControls
                                                                }
                                                            />
                                                        )
                                                    )}
                                                    <th className="d-none d-md-table-cell">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {applicationControls.rows.map(
                                                    (app) => (
                                                        <tr key={app._id}>
                                                            <td>
                                                                <FacultyCell
                                                                    app={app}
                                                                    showEmail
                                                                />
                                                            </td>
                                                            <td>
                                                                <span className="fw-semibold me-1">
                                                                    {
                                                                        app
                                                                            .course
                                                                            ?.subject
                                                                    }{' '}
                                                                    {
                                                                        app
                                                                            .course
                                                                            ?.courseNumber
                                                                    }
                                                                </span>
                                                                <br />
                                                                <small>
                                                                    {
                                                                        app
                                                                            .course
                                                                            ?.title
                                                                    }
                                                                </small>
                                                                {modalityText(
                                                                    app
                                                                ) && (
                                                                    <small className="text-muted d-block d-xl-none">
                                                                        {modalityText(
                                                                            app
                                                                        )}
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN_XL
                                                                }
                                                            >
                                                                {modalityText(
                                                                    app
                                                                ) || '-'}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN
                                                                }
                                                            >
                                                                {crnOf(app) ||
                                                                    '-'}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN
                                                                }
                                                            >
                                                                #
                                                                {getLARank(app)}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN_LG
                                                                }
                                                            >
                                                                {requestedLACount(
                                                                    app
                                                                )}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN_LG
                                                                }
                                                            >
                                                                {getNominees(
                                                                    app
                                                                )}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN_XL
                                                                }
                                                            >
                                                                {app.submittedAt
                                                                    ? formatAnswerDay(
                                                                          app.submittedAt
                                                                      )
                                                                    : '-'}
                                                            </td>
                                                            <td>
                                                                {appStatusPill(
                                                                    app
                                                                )}
                                                            </td>
                                                            <td className="d-none d-md-table-cell">
                                                                <Link
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    to={`/applications/${app._id}`}
                                                                >
                                                                    Review
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </Table>
                                    )}
                                    <TableEmpty
                                        controls={applicationControls}
                                        noun="support requests"
                                        emptyText={`No support requests for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                    />
                                </Card.Body>
                            </Card>
                            <UnsubmittedDrafts
                                programType="la"
                                term={selectedTerm?.code}
                                forceOpen={
                                    activeTab === 'applications' &&
                                    show === DRAFTS_SHOW
                                }
                            />
                        </Tab.Pane>

                        {/* Active LAs Tab */}
                        <Tab.Pane eventKey="active">
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white">
                                    <TableControlsBar
                                        controls={assignmentControls}
                                        noun="LAs"
                                        searchLabel="Search LAs by name, instructor, course or schedule"
                                        searchPlaceholder="Search by LA, instructor, or course..."
                                        statusLabel="Filter LAs by course"
                                        allLabel="All courses"
                                        statusOptions={assignmentCourseOptions}
                                        exportFilename={`la-active-${selectedTerm?.code}.csv`}
                                    />
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                {ASSIGNMENT_COLUMNS.map(
                                                    (column) => (
                                                        <SortableTh
                                                            key={column.key}
                                                            column={column}
                                                            controls={
                                                                assignmentControls
                                                            }
                                                        />
                                                    )
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignmentControls.rows.map(
                                                (la) => (
                                                    <tr key={la._id}>
                                                        <td>
                                                            <strong>
                                                                {la
                                                                    .peerSupporter
                                                                    ?.name ||
                                                                    'Unknown'}
                                                            </strong>
                                                            <small className="text-muted d-none d-md-block">
                                                                {
                                                                    la
                                                                        .peerSupporter
                                                                        ?.email
                                                                }
                                                            </small>
                                                        </td>
                                                        <td
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            {la.instructorName ||
                                                                '-'}
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                bg="secondary"
                                                                className="me-1"
                                                            >
                                                                {
                                                                    la.course
                                                                        ?.subject
                                                                }{' '}
                                                                {
                                                                    la.course
                                                                        ?.courseNumber
                                                                }
                                                            </Badge>
                                                            <br />
                                                            <small>
                                                                {
                                                                    la.course
                                                                        ?.title
                                                                }
                                                            </small>
                                                        </td>
                                                        <td
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            {la.hoursPerWeek ||
                                                                '-'}
                                                        </td>
                                                        <td
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            {la.schedule?.sessions?.map(
                                                                (s, i) => (
                                                                    <small
                                                                        key={i}
                                                                        className="d-block"
                                                                    >
                                                                        {s.day}{' '}
                                                                        {s.time}
                                                                    </small>
                                                                )
                                                            ) || '-'}
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                bg={
                                                                    la.status ===
                                                                    'active'
                                                                        ? 'success'
                                                                        : 'secondary'
                                                                }
                                                                text={badgeTextFor(
                                                                    la.status ===
                                                                        'active'
                                                                        ? 'success'
                                                                        : 'secondary'
                                                                )}
                                                            >
                                                                {la.status ===
                                                                'active'
                                                                    ? 'Active'
                                                                    : la.status}
                                                            </Badge>
                                                            {hiringEnabled &&
                                                                la.sourceCandidacy && (
                                                                    <div className="mt-2">
                                                                        {la
                                                                            .onboarding
                                                                            ?.emailSentAt ? (
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <small className="text-nowrap">
                                                                                    Onboarding
                                                                                    email
                                                                                    sent{' '}
                                                                                    {formatSentDate(
                                                                                        la
                                                                                            .onboarding
                                                                                            .emailSentAt
                                                                                    )}
                                                                                </small>
                                                                                <Dropdown align="end">
                                                                                    <Dropdown.Toggle
                                                                                        variant="outline-primary"
                                                                                        size="sm"
                                                                                        className="onboarding-send-action"
                                                                                        id={`onboarding-more-${la._id}`}
                                                                                        aria-label={`More for ${la.peerSupporter?.name || 'this LA'}`}
                                                                                    >
                                                                                        More
                                                                                    </Dropdown.Toggle>
                                                                                    <Dropdown.Menu>
                                                                                        <Dropdown.Item
                                                                                            as="button"
                                                                                            onClick={() =>
                                                                                                setOnboardingFor(
                                                                                                    la
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            Send
                                                                                            again
                                                                                        </Dropdown.Item>
                                                                                    </Dropdown.Menu>
                                                                                </Dropdown>
                                                                            </div>
                                                                        ) : (
                                                                            <Button
                                                                                variant="primary"
                                                                                size="sm"
                                                                                className="onboarding-send-action"
                                                                                aria-label={`Send onboarding email to ${la.peerSupporter?.name || 'this LA'}`}
                                                                                onClick={() =>
                                                                                    setOnboardingFor(
                                                                                        la
                                                                                    )
                                                                                }
                                                                            >
                                                                                Send
                                                                                onboarding
                                                                                email
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </Table>
                                    <TableEmpty
                                        controls={assignmentControls}
                                        noun="LAs"
                                        emptyText={`No active LA assignments for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                    />
                                </Card.Body>
                            </Card>
                        </Tab.Pane>

                        {/* STEM Courses Tab */}
                        <Tab.Pane eventKey="courses">
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white">
                                    <TableControlsBar
                                        controls={courseControls}
                                        noun="courses"
                                        searchLabel="Search courses by code, section or title"
                                        searchPlaceholder="Search by course or section..."
                                        statusLabel="Filter courses by staffing"
                                        allLabel="All courses"
                                        statusOptions={
                                            hiringEnabled && positions
                                                ? COURSE_STAFFING_OPTIONS
                                                : null
                                        }
                                        exportFilename={`la-courses-${selectedTerm?.code}.csv`}
                                    />
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                {courseColumns.map((column) => (
                                                    <SortableTh
                                                        key={column.key}
                                                        column={column}
                                                        controls={
                                                            courseControls
                                                        }
                                                    />
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {courseControls.rows.map(
                                                (course) => {
                                                    const coursePositions =
                                                        positionsByCourseId.get(
                                                            course._id
                                                        ) || [];
                                                    return (
                                                        <tr key={course._id}>
                                                            <td>
                                                                <Badge
                                                                    bg="secondary"
                                                                    className="me-1"
                                                                >
                                                                    {
                                                                        course.subject
                                                                    }{' '}
                                                                    {
                                                                        course.courseNumber
                                                                    }
                                                                </Badge>
                                                                {course.section && (
                                                                    <small className="text-muted">
                                                                        {' '}
                                                                        Section{' '}
                                                                        {
                                                                            course.section
                                                                        }
                                                                    </small>
                                                                )}
                                                                <br />
                                                                <small>
                                                                    {
                                                                        course.title
                                                                    }
                                                                </small>
                                                            </td>
                                                            {hiringEnabled && (
                                                                <td>
                                                                    {renderPositions(
                                                                        coursePositions
                                                                    )}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                    </Table>
                                    <TableEmpty
                                        controls={courseControls}
                                        noun="courses"
                                        emptyText={`No LA-eligible courses for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                    />
                                </Card.Body>
                            </Card>
                        </Tab.Pane>

                        {/* Student Applicants Tab. Gated with the same flag as the
                        routes it navigates into: with the flag off the list is
                        empty and Review leads nowhere, so the tab would read
                        "Student Applicants (0)" and advertise a feature that
                        is not there. */}
                        {studentApplicationEnabled && (
                            <Tab.Pane eventKey="student-applications">
                                {studentAppsFailed ? (
                                    <LoadError
                                        what="the student applicants"
                                        onRetry={() =>
                                            loadTermData(selectedTerm.code)
                                        }
                                    />
                                ) : (
                                    <Card className="border-0 shadow-sm">
                                        <Card.Header className="bg-white">
                                            <TableControlsBar
                                                controls={
                                                    studentApplicationControls
                                                }
                                                noun="applicants"
                                                searchLabel="Search applicants by name, email or course"
                                                searchPlaceholder="Search by name, email, or course..."
                                                statusLabel="Filter by student application status"
                                                statusOptions={
                                                    STUDENT_APPLICATION_STATUS_OPTIONS
                                                }
                                                exportFilename={`la-student-applicants-${selectedTerm?.code}.csv`}
                                            />
                                        </Card.Header>
                                        <Card.Body className="p-0">
                                            <Table
                                                responsive
                                                hover
                                                className="mb-0"
                                            >
                                                <thead className="bg-light">
                                                    <tr>
                                                        {STUDENT_APPLICATION_COLUMNS.map(
                                                            (column) => (
                                                                <SortableTh
                                                                    key={
                                                                        column.key
                                                                    }
                                                                    column={
                                                                        column
                                                                    }
                                                                    controls={
                                                                        studentApplicationControls
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                        <th
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {studentApplicationControls.rows.map(
                                                        (app) => (
                                                            <tr key={app._id}>
                                                                <td>
                                                                    <strong>
                                                                        {app
                                                                            .student
                                                                            ?.name ||
                                                                            'Unknown'}
                                                                    </strong>
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        className="d-block d-md-none mt-2"
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/student-applications/${app._id}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Review
                                                                    </Button>
                                                                </td>
                                                                <td
                                                                    className={
                                                                        SECONDARY_COLUMN
                                                                    }
                                                                >
                                                                    <small className="text-muted">
                                                                        {app
                                                                            .student
                                                                            ?.email ||
                                                                            '-'}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    {app
                                                                        .coursePreferences
                                                                        ?.length >
                                                                    0
                                                                        ? app.coursePreferences.map(
                                                                              (
                                                                                  pref,
                                                                                  idx
                                                                              ) => (
                                                                                  <Badge
                                                                                      key={
                                                                                          idx
                                                                                      }
                                                                                      bg="secondary"
                                                                                      className="me-1 mb-1"
                                                                                  >
                                                                                      {pref
                                                                                          .course
                                                                                          ?.subject ||
                                                                                          ''}{' '}
                                                                                      {pref
                                                                                          .course
                                                                                          ?.courseNumber ||
                                                                                          ''}
                                                                                  </Badge>
                                                                              )
                                                                          )
                                                                        : positionChoiceSummary(
                                                                              app
                                                                          ) ||
                                                                          '-'}
                                                                </td>
                                                                <td
                                                                    className={
                                                                        SECONDARY_COLUMN
                                                                    }
                                                                >
                                                                    {app.submittedAt
                                                                        ? formatAnswerDay(
                                                                              app.submittedAt
                                                                          )
                                                                        : '-'}
                                                                </td>
                                                                <td>
                                                                    <StatusPill
                                                                        family="studentApplication"
                                                                        value={
                                                                            app.status
                                                                        }
                                                                    />
                                                                </td>
                                                                <td
                                                                    className={
                                                                        SECONDARY_COLUMN_LG
                                                                    }
                                                                >
                                                                    <small>
                                                                        {workStudyText(
                                                                            app
                                                                        )}
                                                                    </small>
                                                                </td>
                                                                <td
                                                                    className={
                                                                        SECONDARY_COLUMN_LG
                                                                    }
                                                                >
                                                                    <small>
                                                                        {otherRoleInterestText(
                                                                            app
                                                                                .laDetails
                                                                                ?.otherRoleInterest
                                                                        )}
                                                                    </small>
                                                                </td>
                                                                <td
                                                                    className={
                                                                        SECONDARY_COLUMN
                                                                    }
                                                                >
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/student-applications/${app._id}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Review
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </Table>
                                            <TableEmpty
                                                controls={
                                                    studentApplicationControls
                                                }
                                                noun="applicants"
                                                emptyText={`No student applications found for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                            />
                                        </Card.Body>
                                    </Card>
                                )}
                            </Tab.Pane>
                        )}

                        <Tab.Pane eventKey="decisions" mountOnEnter>
                            <ProgramDecisionsList
                                programType="la"
                                term={selectedTerm?.code}
                                termName={termDisplayName(selectedTerm)}
                            />
                        </Tab.Pane>

                        {hiringEnabled && (
                            <Tab.Pane eventKey="positions" mountOnEnter>
                                <CoordinatorPositionsTab
                                    programType="la"
                                    term={selectedTerm?.code}
                                    termName={termDisplayName(selectedTerm)}
                                    show={
                                        activeTab === 'positions' ? show : null
                                    }
                                    onClearShow={clearShow}
                                    onPositionChanged={handlePositionChanged}
                                    decisionsReleased={
                                        selectedTerm?.programWindows?.la
                                            ?.decisionsReleased
                                    }
                                />
                            </Tab.Pane>
                        )}
                        <Tab.Pane eventKey="settings" mountOnEnter>
                            <CoordinatorSettings
                                focusKey={
                                    activeTab === 'settings' ? show : null
                                }
                                sections={settingsSections}
                                onSelect={(key) => selectTab('settings', key)}
                            />
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            )}
            {onboardingFor && (
                <OnboardingEmailDialog
                    assignment={onboardingFor}
                    onHide={() => setOnboardingFor(null)}
                    onSent={(sent) => {
                        setAssignments((prev) =>
                            prev.map((a) =>
                                a._id === onboardingFor._id
                                    ? {
                                          ...a,
                                          onboarding: {
                                              ...a.onboarding,
                                              emailSentAt: sent.sentAt,
                                              emailSentBy: sent.sentBy,
                                          },
                                      }
                                    : a
                            )
                        );
                        setOnboardingFor(null);
                    }}
                />
            )}
        </Container>
    );
}

export default LACoordinatorDashboard;
